import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useCallback } from "react";

import { Event } from "0.Mine/3.Interfaces/Event";

export const useEventCalendarLogic = (
  onSelectEvent: (event: Event) => void,
  onSelectSlot: ({ start, end }: { start: Date; end: Date }) => void
) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const styles = {
    container: {
      height: isMobile ? "500px" : "650px",
      width: "100%",
      display: "flex",
      flexDirection: "column" as const,
      position: "relative" as const,
    },
    calendar: {
      flexGrow: 1,
      "& .fc": {
        height: "100%",
        width: "100%",

        // Colonna oraria più larga
        "& .fc-timegrid-slot-label": {
          minWidth: "60px", // Allarga la colonna degli orari
          textAlign: "right",
          paddingRight: "6px",
          fontSize: "0.75rem",
          color: "#444",
        },

        // Slot orari più compatti verticalmente
        "& .fc-timegrid-slot": {
          height: "28px", // Riduce l'altezza degli slot
        },

        // Eventi compatti
        "& .fc-event": {
          padding: "2px 6px",
          fontSize: "0.75rem",
          borderRadius: "5px",
          background: "linear-gradient(310deg, #36376d, #5a6eb4)",
          color: "#fff",
          border: "none",
        },

        // Compatta intestazioni
        "& .fc-col-header-cell-cushion": {
          padding: "4px 0",
          fontSize: "0.75rem",
          color: "#666",
        },

        // Compatta intestazioni laterali
        "& .fc-timegrid-axis": {
          padding: "2px 4px",
        },

        // Toolbar e bottoni su mobile
        ...(isMobile && {
          "& .fc-toolbar-title": {
            fontSize: "1rem",
          },
          "& .fc-button": {
            padding: "0.3em 0.5em",
            fontSize: "0.7rem",
          },
        }),
      },
    },
  };

  const handleSelect = useCallback(
    (selectInfo: any) => {
      if (!selectInfo || !selectInfo.start || !selectInfo.end) {
        console.error("Invalid selection info:", selectInfo);
        return;
      }

      onSelectSlot({
        start: selectInfo.start,
        end: selectInfo.end,
      });
    },
    [onSelectSlot]
  );

  const handleEventClick = useCallback(
    (clickInfo: any) => {
      try {
        if (!clickInfo || !clickInfo.event) {
          console.error("Invalid click info:", clickInfo);
          return;
        }

        const { event } = clickInfo;

        // Validate date fields
        if (!event.start || !(event.start instanceof Date)) {
          console.error("Event has invalid start date:", event);
          return;
        }

        // Convert FullCalendar event to your Event type
        const eventData: Event = {
          id: event.id || "",
          title: event.title || "",
          start: event.start,
          end: event.end || new Date(event.start.getTime() + 3600000), // Default 1 hour if no end date
          description: event.extendedProps?.description || "",
          location: event.extendedProps?.location || "",
          remindOnWhatsapp: event.extendedProps?.remindOnWhatsapp || false,
          remindViaEmail: event.extendedProps?.remindViaEmail || false,
          sharedWith: event.extendedProps?.sharedWith || [],
          userId: event.extendedProps?.userId || "",
          vendorId: event.extendedProps?.vendorId || [],
        };

        onSelectEvent(eventData);
      } catch (error) {
        console.error("Error handling event click:", error);
      }
    },
    [onSelectEvent]
  );

  const handleEventDidMount = useCallback(
    (info: any) => {
      if (!info || !info.event || !info.el) return;

      try {
        const tooltip = document.createElement("div");
        tooltip.className = "custom-event-tooltip";
        tooltip.innerHTML = `
              <div style="
                width: 100%;
                background: linear-gradient(310deg, #36376d, #5a6eb4);
                color: white;
                font-weight: bold;
                padding: 6px 10px;
                font-size: 0.95rem;
                border-top-left-radius: 6px;
                border-top-right-radius: 6px;
              ">
                ${info.event.title || "Senza titolo"}
              </div>
              <div style="padding: 8px 10px; background: white; border-bottom-left-radius: 6px; border-bottom-right-radius: 6px; line-height: 18px;">
                ${
                  info.event.extendedProps?.description
                    ? `<div style="margin-bottom: 4px;">${info.event.extendedProps.description}</div>`
                    : ""
                }
                ${
                  info.event.extendedProps?.location
                    ? `
                  <div style="display: flex; align-items: center; gap: 6px; font-size: .7rem; line-height: 14px; border-top: 1px solid #dddddd; padding-bottom:4px;  padding-top:4px; margin-top:5px;">
                    <span>📍</span>
                    <span>${info.event.extendedProps.location}</span>
                  </div>`
                    : ""
                }
              </div>
            `;

        Object.assign(tooltip.style, {
          position: "absolute",
          zIndex: "9999",
          background: "#fff",
          borderRadius: "6px",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
          fontSize: "0.85rem",
          maxWidth: "220px",
          pointerEvents: "none",
        });

        let isTooltipVisible = false;

        const showTooltip = () => {
          if (isTooltipVisible) return;
          document.body.appendChild(tooltip);
          const rect = info.el.getBoundingClientRect();
          tooltip.style.top = `${rect.top + window.scrollY - 10}px`;
          tooltip.style.left = `${rect.left + window.scrollX + rect.width / 2}px`;
          isTooltipVisible = true;
        };

        const hideTooltip = () => {
          if (tooltip.parentNode) tooltip.parentNode.removeChild(tooltip);
          isTooltipVisible = false;
        };

        // Desktop: hover
        info.el.addEventListener("mouseenter", showTooltip);
        info.el.addEventListener("mouseleave", hideTooltip);

        // Mobile: touch
        info.el.addEventListener("touchstart", () => {
          setTimeout(showTooltip, 50);
          setTimeout(hideTooltip, 2500);
        });

        info.el.style.cursor = "pointer";
      } catch (error) {
        console.error("Errore in eventDidMount:", error);
      }
    },
    [onSelectEvent]
  );

  return {
    styles,
    handleSelect,
    handleEventClick,
    handleEventDidMount,
  };
};
